import { general, useEditorStore } from '@modules/editor';
import { reversionElement } from '@modules/editor/src/lib/element';
import { useEditorRuntimeStore } from '@modules/editor/src/stores/useEditorRuntimeStore';
import { Mutex } from 'async-mutex';
import { createSelectorHooks } from 'auto-zustand-selectors-hook';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

import { trackEvent } from '$/logger';
import {
  mergeHarmonyOverridesPreservingManual,
  resolveActiveHarmony,
  resolvePreviousHarmonyWithMaterials,
  shuffleHarmonyOverrides,
} from '$/pages/InspirationEditorPage/lib/harmonyUtils';
import {
  buildMaterialMappings,
  type InspirationGeometry,
  type MaterialMapping,
} from '$/pages/InspirationEditorPage/lib/materialMappings';
import {
  isComponentVisible,
  getVisibleComponentIds,
} from '$/pages/InspirationEditorPage/lib/selectors';
import * as webglRenderer from '$/pages/InspirationEditorPage/lib/webglRenderer';
import {
  inspirationPlugin,
  type InspirationImageElement,
} from '$/pages/InspirationEditorPage/plugins/inspiration';
import { queryClient } from '$/services/client';
import {
  type DetailedInspiration,
  type InspirationComponent,
} from '$/services/usecases/inspirations/mapper/inspiration.schema';
import { type HarmonyWithMaterials } from '$/services/usecases/materials/mapper/harmonies';
import { type Material } from '$/services/usecases/materials/mapper/materials';
import {
  harmonyByIdQuery,
  materialsQuery,
} from '$/services/usecases/materials/queries';
import { type LayerRotation } from '$/services/usecases/projects/mapper/inspirationProjects';
import { useCollectionStore } from '$/stores/useCollectionStore';
import { imageDataToCanvas } from '$/utils/canvas';
import { trackMaterialRendering } from '$/utils/piwikUtils';

export type InspirationEditorState = {
  inspiration: DetailedInspiration | undefined;
  projectId?: string;
  renderedImagePreviewUrls: (string | null)[];
  editMasterData: boolean;
  activeComponentId: string | undefined;
  hoveredComponentId: string | undefined;
  materialMappingOverrides: Record<string, MaterialMapping>[];
  cornerOverwrites: Record<number, Record<string, InspirationGeometry>>;
  currentMappingIndex: number;
  currentViewId: number;
  perspectiveFullscreen: boolean;
  materialMappingLabelOrder: string[];
  rendererInitialized: boolean;
  mappingsInitialized: boolean;
  initialRenderReady: boolean;
  isRendering: boolean;
  inspirationFieldOverwrites: Partial<
    Pick<DetailedInspiration, 'category' | 'mainWallComponentId'>
  >;
  componentOverwrites: Record<string, Partial<InspirationComponent>>;
  showTestStructure: boolean;
  initialHarmonyIds: string[] | undefined;
};

type InspirationEditorActions = {
  toggleActiveComponent: (component?: string) => void;
  changeComponentMaterial: (material: Material) => void;
  setComponentRotation: (rotation: LayerRotation) => void;
  clearActiveComponentMaterial: () => void;
  setCornerOverwrites: (
    viewId: number,
    componentId: string,
    geometry: InspirationGeometry,
  ) => void;
  initializeRenderer: (inspiration: DetailedInspiration) => Promise<void>;
  initializeMaterialMappings: () => Promise<void>;
  areCoordsInMask: (mask: string, x: number, y: number) => Promise<boolean>;
  getAvailableMasks: () => Record<string, InspirationComponent>;
  render: () => Promise<boolean>;
  setHarmony: (harmonyId: string) => Promise<void>;
  shuffleHarmony: () => Promise<void>;
  exportRenderedImage: (opt: {
    viewId: number;
    mappingIndex: number;
    targetWidth?: number;
    targetHeight?: number;
    maxSize?: number;
  }) => Promise<HTMLCanvasElement | null>;
  togglePerspectiveFullscreen: () => void;
  switchMaterialMapping: (index: number) => void;
  switchView: (viewId: number) => Promise<void>;
  deleteMaterialMapping: (index: number) => void;
  addMaterialMapping: () => void;
  setMasterDataEditMode: (mode: boolean) => void;
  setInspirationCategory: (category: string) => void;
  setMainWallComponentId: (componentId: string | null) => void;
  setComponentOverwrite: <K extends keyof InspirationComponent>(
    componentId: string,
    field: K,
    value: InspirationComponent[K],
  ) => void;
  toggleComponentVisibility: (componentId: string) => void;
  toggleTestStructure: () => void;
  resetSession: () => void;
  initialize: (init: Partial<InspirationEditorState>) => void;
};

const initialState: InspirationEditorState = {
  inspiration: undefined,
  projectId: undefined,
  renderedImagePreviewUrls: [null, null, null],
  editMasterData: false,
  activeComponentId: undefined,
  hoveredComponentId: undefined,
  materialMappingOverrides: [],
  cornerOverwrites: {},
  currentMappingIndex: 0,
  currentViewId: 0,
  perspectiveFullscreen: false,
  materialMappingLabelOrder: ['A', 'B', 'C'],
  rendererInitialized: false,
  mappingsInitialized: false,
  initialRenderReady: false,
  isRendering: false,
  inspirationFieldOverwrites: {},
  componentOverwrites: {},
  showTestStructure: false,
  initialHarmonyIds: undefined,
};

export const getEffectiveMainWallComponentId = (
  state: InspirationEditorState,
) =>
  state.inspirationFieldOverwrites.mainWallComponentId === undefined
    ? (state.inspiration?.mainWallComponentId ?? null)
    : state.inspirationFieldOverwrites.mainWallComponentId;

const componentMatchesMaterialType = (
  material: Material | undefined,
  component: InspirationComponent,
): boolean => {
  if (!material) return false;

  switch (material?.type[0]) {
    case 'color_tint':
      return component.category === 'wall';
    case 'floor_texture':
      return component.category === 'floor';
    case 'wall_texture':
      return component.category === 'wall';
    case 'facade_texture':
      return component.category === 'facade';
    default:
      return false;
  }
};

export const getMaterialOverrides = async (
  inspiration: DetailedInspiration,
  harmony?: HarmonyWithMaterials | null,
  activeMaterialId?: string | null,
) => {
  const overrides: Record<string, MaterialMapping> = {};

  let activeMaterial: Material | undefined;
  if (activeMaterialId) {
    const res = await queryClient.ensureQueryData(
      materialsQuery({ ids: [activeMaterialId] }),
    );
    activeMaterial = res.results?.at(0);
  }

  for (const [id, component] of Object.entries<InspirationComponent>(
    inspiration.components,
  )) {
    if (componentMatchesMaterialType(activeMaterial, component)) {
      overrides[id] = { material: activeMaterial!, rotation: 'vertical' };
    } else {
      const harmonyMaterial = harmony?.inspirationMaterials[component.key];

      if (harmonyMaterial) {
        overrides[id] = {
          material: harmonyMaterial,
          rotation: 'vertical',
          harmonyMaterialIndex: harmony.materials.findIndex(
            (m) => m.id === harmonyMaterial.id,
          ),
        };
      } else if (component.initialMaterialId) {
        const initialMaterial =
          inspiration.materials[component.initialMaterialId];
        if (initialMaterial) {
          overrides[id] = { material: initialMaterial, rotation: 'vertical' };
        }
      }
    }
  }

  return overrides;
};

const renderingMutex = new Mutex();
const initMutex = new Mutex();

export const inspirationEditorStore = create<
  InspirationEditorState & InspirationEditorActions
>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,
    toggleActiveComponent: (component) => {
      if (!component) {
        set({ activeComponentId: undefined });
        if (get().showTestStructure) get().render();
        return;
      }

      set((state) => ({
        activeComponentId:
          state.activeComponentId === component ? undefined : component,
      }));

      const materialMapping =
        get().materialMappingOverrides[get().currentMappingIndex][component];

      if (materialMapping) {
        useCollectionStore.setState({
          activeType: materialMapping.material?.type[0] ?? 'color_tint',
          appliedFilterOptions: [
            { option: 'producer', value: materialMapping.material.producer },
            {
              option: 'collection',
              value: materialMapping.material.collection,
            },
          ],
        });
      }

      if (get().showTestStructure) get().render();
    },
    changeComponentMaterial: (material) => {
      set((state) => {
        if (!state.activeComponentId) return state;

        const newOverrides = [...state.materialMappingOverrides];
        const currentMapping = { ...newOverrides[state.currentMappingIndex] };

        const existingMapping = currentMapping[state.activeComponentId];
        currentMapping[state.activeComponentId] = {
          ...existingMapping,
          material,
          rotation: existingMapping?.rotation ?? 'vertical',
        };

        newOverrides[state.currentMappingIndex] = currentMapping;

        return {
          materialMappingOverrides: newOverrides,
        };
      });

      useCollectionStore.setState({
        activeType: material.type[0],
        appliedFilterOptions: [
          { option: 'producer', value: material.producer },
          {
            option: 'collection',
            value: material.collection,
          },
        ],
      });

      trackMaterialRendering('Inspiration', material);

      get().render();
    },
    setComponentRotation: (rotation) => {
      const state = get();
      if (!state.activeComponentId) return;

      set((state) => {
        const newOverrides = [...state.materialMappingOverrides];
        const currentMapping = { ...newOverrides[state.currentMappingIndex] };

        const existingMapping = currentMapping[state.activeComponentId!];
        currentMapping[state.activeComponentId!] = {
          ...existingMapping,
          rotation,
        };

        newOverrides[state.currentMappingIndex] = currentMapping;

        return {
          materialMappingOverrides: newOverrides,
        };
      });

      get().render();
    },
    clearActiveComponentMaterial: () => {
      const {
        activeComponentId,
        currentMappingIndex,
        materialMappingOverrides,
      } = get();
      const currentMapping = materialMappingOverrides[currentMappingIndex];
      if (!activeComponentId || !currentMapping?.[activeComponentId]) return;

      const newMapping = { ...currentMapping };
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete newMapping[activeComponentId];

      const newOverrides = [...materialMappingOverrides];
      newOverrides[currentMappingIndex] = newMapping;

      set({ materialMappingOverrides: newOverrides });
      get().render();
    },
    setCornerOverwrites: (viewId, componentId, geometry) => {
      set((state) => ({
        cornerOverwrites: {
          ...state.cornerOverwrites,
          [viewId]: {
            ...state.cornerOverwrites[viewId],
            [componentId]: geometry,
          },
        },
      }));
    },
    initializeRenderer: async (inspiration) => {
      await initMutex.waitForUnlock();
      await initMutex.acquire();

      set({ rendererInitialized: false });

      try {
        const state = get();
        const loaded = await webglRenderer.loadViewAssets(
          inspiration,
          state,
          state.currentViewId,
        );
        if (!loaded) return;

        const canvasSize = webglRenderer.getCanvasSize();
        if (canvasSize) {
          useEditorRuntimeStore.setState({
            panConstraintElementId: 'preview',
          });

          if (useEditorStore.getState().elements.has('preview')) {
            general.actions.deleteElements(['preview']);
          }

          inspirationPlugin.actions.addCenterImage(
            { width: canvasSize.width, height: canvasSize.height },
            { id: 'preview' },
          );
          general.actions.zoomToFit();
        }

        set({ rendererInitialized: true });
      } finally {
        initMutex.release();
      }
    },
    initializeMaterialMappings: async () => {
      const images: string[] = [];

      const firstViewId = get().inspiration?.views[0]?.id ?? 0;

      for (let i = 0; i < get().materialMappingOverrides.length; i++) {
        const exportedImage = await get().exportRenderedImage({
          mappingIndex: i,
          viewId: firstViewId,
          targetWidth: 1080,
        });
        if (!exportedImage) return;
        images.push(exportedImage.toDataURL());
      }

      set({ mappingsInitialized: true, renderedImagePreviewUrls: images });
    },
    areCoordsInMask: async (mask, x, y) => {
      return webglRenderer.areCoordsInMask(mask, x, y);
    },
    getAvailableMasks: () => {
      const state = get();
      const { inspiration } = state;
      if (!inspiration) return {};

      return getVisibleComponentIds(state)
        .filter((id) => inspiration.render.masks[id])
        .reduce<Record<string, InspirationComponent>>((acc, id) => {
          const component = inspiration.components[id];
          if (component) acc[`mask_${id}`] = component;
          return acc;
        }, {});
    },
    render: async () => {
      await renderingMutex.waitForUnlock();
      await renderingMutex.acquire();

      set({ isRendering: true });

      const { inspiration } = get();

      if (!inspiration) {
        renderingMutex.release();
        set({ isRendering: false });
        return false;
      }

      const mappings = buildMaterialMappings(get());

      const imageObject = general.actions.getElement<
        InspirationImageElement | undefined
      >('preview');

      if (!imageObject) {
        renderingMutex.release();
        set({ isRendering: false });
        return false;
      }

      try {
        const rawImageData = await webglRenderer.render(mappings);

        const canvasSize = webglRenderer.getCanvasSize();

        if (!canvasSize) {
          return false;
        }

        const imageData = new ImageData(
          new Uint8ClampedArray(rawImageData),
          canvasSize.width,
          canvasSize.height,
        );

        const previewCanvas = imageObject.image;
        previewCanvas.width = canvasSize.width;
        previewCanvas.height = canvasSize.height;
        previewCanvas.getContext('2d')!.putImageData(imageData, 0, 0);

        set((state) => {
          const aspectRatio = imageData.width / imageData.height;

          const resized = imageDataToCanvas({
            imageData,
            targetWidth: 1080,
            targetHeight: Math.round(1080 / aspectRatio),
          });

          const newRenderedImages = [...state.renderedImagePreviewUrls];
          newRenderedImages[get().currentMappingIndex] = resized.toDataURL();
          return { renderedImagePreviewUrls: newRenderedImages };
        });

        general.actions.updateElement(
          'preview',
          reversionElement(imageObject, ['canvas']),
        );

        return true;
      } finally {
        renderingMutex.release();
        set({ isRendering: false });
      }
    },
    setHarmony: async (harmonyId: string) => {
      const harmony = (await queryClient.ensureQueryData(
        harmonyByIdQuery(harmonyId),
      ))!;
      const { inspiration } = get();
      if (!inspiration) return;

      const mappingIndex = get().currentMappingIndex;
      const currentOverrides = get().materialMappingOverrides[mappingIndex];
      const initialHarmony = await resolvePreviousHarmonyWithMaterials(
        currentOverrides,
        get().initialHarmonyIds?.[mappingIndex],
      );

      const isSameInspiration = harmony.inspirationId === inspiration.id;

      if (isSameInspiration) {
        // same inspirationId will overwrite initialHarmonyId and ensure DesignGuide overwrites
        const overrides = await mergeHarmonyOverridesPreservingManual(
          inspiration,
          initialHarmony,
          harmony,
          currentOverrides,
        );

        set((state) => {
          const materialMappingOverrides = [...state.materialMappingOverrides];
          materialMappingOverrides[mappingIndex] = overrides;

          const initialHarmonyIds = state.initialHarmonyIds
            ? [...state.initialHarmonyIds]
            : undefined;
          if (initialHarmonyIds) {
            initialHarmonyIds[mappingIndex] = harmonyId;
          }

          return {
            materialMappingOverrides,
            initialHarmonyIds,
          };
        });
      } else {
        set((state) => {
          const materialMappingOverrides = [...state.materialMappingOverrides];
          const overrides = { ...materialMappingOverrides[mappingIndex] };

          for (const compId in overrides) {
            const component = inspiration.components[compId];
            if (!component) continue;

            const mIdx = overrides[compId].harmonyMaterialIndex;
            if (
              mIdx !== undefined &&
              mIdx > -1 &&
              mIdx < harmony.materials.length
            )
              overrides[compId] = {
                ...overrides[compId],
                material: harmony.materials[mIdx],
              };
          }

          materialMappingOverrides[mappingIndex] = overrides;

          return {
            materialMappingOverrides,
          };
        });
      }

      await get().render();
    },
    shuffleHarmony: async () => {
      const mappingIndex = get().currentMappingIndex;
      const overrides = get().materialMappingOverrides[mappingIndex];

      const activeHarmony = await resolveActiveHarmony(overrides);
      if (!activeHarmony) return;

      const shuffled = shuffleHarmonyOverrides(
        overrides,
        activeHarmony.materials,
      );

      set((state) => {
        const materialMappingOverrides = [...state.materialMappingOverrides];
        materialMappingOverrides[mappingIndex] = shuffled;
        return { materialMappingOverrides };
      });

      trackEvent('Tools-DesignGuide', 'Click', 'shuffle');

      await get().render();
    },
    exportRenderedImage: async ({
      viewId,
      mappingIndex,
      targetWidth,
      targetHeight,
      maxSize,
    }): Promise<HTMLCanvasElement | null> => {
      const { inspiration, currentViewId } = get();

      if (!inspiration) return null;

      const needsTemporaryViewAssets = viewId !== currentViewId;

      if (needsTemporaryViewAssets || !webglRenderer.isRendererInitialized()) {
        const loaded = await initMutex.runExclusive(() =>
          webglRenderer.loadViewAssets(inspiration, get(), viewId),
        );
        if (!loaded) return null;
      }

      await renderingMutex.acquire();
      try {
        const mappings = buildMaterialMappings(get(), {
          viewIdOverride: viewId,
          mappingOverride: mappingIndex,
        });

        const canvasSize = webglRenderer.getCanvasSize();
        if (!canvasSize) return null;

        const { width: originalWidth, height: originalHeight } = canvasSize;

        let exportWidth = targetWidth;
        let exportHeight = targetHeight;
        if (maxSize != null && maxSize > 0) {
          const longerSide = Math.max(originalWidth, originalHeight);
          if (longerSide > maxSize) {
            const scale = maxSize / longerSide;
            exportWidth = Math.round(originalWidth * scale);
            exportHeight = Math.round(originalHeight * scale);
          } else {
            exportWidth = originalWidth;
            exportHeight = originalHeight;
          }
        }

        const rawImageData = await webglRenderer.render(mappings);
        const imageData = new ImageData(
          new Uint8ClampedArray(rawImageData),
          originalWidth,
          originalHeight,
        );
        return imageDataToCanvas({
          imageData,
          targetWidth: exportWidth,
          targetHeight: exportHeight,
        });
      } finally {
        renderingMutex.release();

        if (needsTemporaryViewAssets) {
          await initMutex.runExclusive(() =>
            webglRenderer.loadViewAssets(
              inspiration,
              get(),
              get().currentViewId,
            ),
          );
          await get().render();
        }
      }
    },
    togglePerspectiveFullscreen: () => {
      set({ perspectiveFullscreen: !get().perspectiveFullscreen });
    },
    switchMaterialMapping: (index) => {
      set({ currentMappingIndex: index });
      get().render();
    },
    switchView: async (viewId) => {
      const { inspiration } = get();
      if (!inspiration) return;

      set({ currentViewId: viewId });
      await get().initializeRenderer(inspiration);
      await get().render();
    },
    addMaterialMapping: () => {
      const state = get();
      const {
        currentMappingIndex,
        materialMappingOverrides,
        initialHarmonyIds,
      } = state;
      const initialHarmonyId = initialHarmonyIds?.[currentMappingIndex];

      const newMappings = [
        ...materialMappingOverrides,
        materialMappingOverrides[currentMappingIndex],
      ];

      set({
        materialMappingOverrides: newMappings,
        currentMappingIndex: newMappings.length - 1,
        initialHarmonyIds:
          initialHarmonyIds && initialHarmonyId
            ? [...initialHarmonyIds, initialHarmonyId]
            : initialHarmonyIds,
      });

      get().render();
    },
    deleteMaterialMapping: (index: number) => {
      const state = get();
      const newMapping = [...state.materialMappingOverrides];
      newMapping.splice(index, 1);

      const newInitialHarmonyIds = state.initialHarmonyIds?.filter(
        (_, i) => i !== index,
      );

      const activeIndex = state.currentMappingIndex;

      if (index <= activeIndex && activeIndex !== 0) {
        set({ currentMappingIndex: activeIndex - 1 });
        get().render();
      }

      const label = state.materialMappingLabelOrder[index];
      const newOrder = [...state.materialMappingLabelOrder];
      newOrder.splice(index, 1);
      newOrder.push(label);

      set({
        materialMappingOverrides: newMapping,
        materialMappingLabelOrder: newOrder,
        initialHarmonyIds: newInitialHarmonyIds,
      });
    },
    setMasterDataEditMode: (mode: boolean) => {
      set({ editMasterData: mode });
    },
    setInspirationCategory: (category: string) => {
      set((state) => ({
        inspirationFieldOverwrites: {
          ...state.inspirationFieldOverwrites,
          category,
        },
      }));
    },
    setMainWallComponentId: (componentId) => {
      set((state) => ({
        inspirationFieldOverwrites: {
          ...state.inspirationFieldOverwrites,
          mainWallComponentId: componentId,
        },
      }));
    },
    setComponentOverwrite: (componentId, field, value) => {
      set((state) => ({
        componentOverwrites: {
          ...state.componentOverwrites,
          [componentId]: {
            ...state.componentOverwrites[componentId],
            [field]: value,
          },
        },
      }));
    },
    toggleComponentVisibility: (componentId) => {
      set((state) => {
        const currentOverwrite = state.componentOverwrites[componentId] ?? {};
        const currentVisibility = isComponentVisible(state, componentId);

        return {
          componentOverwrites: {
            ...state.componentOverwrites,
            [componentId]: {
              ...currentOverwrite,
              isVisible: !currentVisibility,
            },
          },
        };
      });

      get().render();
    },
    toggleTestStructure: () => {
      set((state) => ({ showTestStructure: !state.showTestStructure }));

      get().render();
    },
    resetSession: () => set({ ...initialState }),
    // atomically resets the session and applies the given state,
    // so no intermediate reset state is ever observed
    initialize: (init) => set({ ...initialState, ...init }),
  })),
);

export const useInspirationEditorStore = createSelectorHooks(
  inspirationEditorStore,
);
